import React, { useEffect, useState } from "react";
import logo from "../assets/logo03132025-removebg.png";
import goldlogo from "../assets/naturx_visiting_card__2_-removebg.png";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Menu,
  X,
  Phone,
} from "lucide-react";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false); // mobile drawer
  const [selectedItem, setSelectedItem] = useState("Home");
  const [openSubMenu, setOpenSubMenu] = useState(null); // top nav submenu (Products)
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [data, setData] = useState([]);
  const [cartItemCount, setCartItemCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // ---- Main categories (the row under the navbar) ----
  const [mainCategories, setMainCategories] = useState([]);

  // Which main category's dropdown is currently open
  const [openMainCatId, setOpenMainCatId] = useState(null);

  // collections keyed by main_cat_id so we don't refetch every click:
  // { [main_cat_id]: { loading, error, items: [...] } }
  const [collectionsByCat, setCollectionsByCat] = useState({});

  // ---- Contact / extra details ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.naturx.co.in/api/get_extra_details",
        );
        const result = await response.json();
        if (result.status === 1) {
          setData(result.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // ---- Fetch main categories on mount ----
  useEffect(() => {
    const fetchMainCategories = async () => {
      try {
        const response = await fetch(
          "https://webadmin.naturx.co.in/api/get_main_category",
        );
        if (!response.ok) {
          throw new Error("Failed to fetch main categories");
        }
        const result = await response.json();
        setMainCategories(result.data || []);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchMainCategories();
  }, []);

  // ---- Scroll state ----
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ---- Cart count ----
  useEffect(() => {
    const existingCartItems =
      JSON.parse(localStorage.getItem("cartItems")) || [];
    setCartItemCount(existingCartItems.length);
  }, []);

  // ---- Lock body scroll while mobile drawer is open ----
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ---- Fetch collections for a given main category (cached) ----
  const fetchCollectionsForCat = async (mainCatId) => {
    // already loaded successfully? skip refetch
    if (collectionsByCat[mainCatId]?.items?.length) return;

    setCollectionsByCat((prev) => ({
      ...prev,
      [mainCatId]: { loading: true, error: null, items: [] },
    }));

    try {
      const response = await fetch(
        `https://webadmin.naturx.co.in/api/get_category?main_cat_id=${mainCatId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const result = await response.json();
      setCollectionsByCat((prev) => ({
        ...prev,
        [mainCatId]: {
          loading: false,
          error: null,
          items: result.data || [],
        },
      }));
    } catch (err) {
      console.error(err.message);
      setCollectionsByCat((prev) => ({
        ...prev,
        [mainCatId]: { loading: false, error: err.message, items: [] },
      }));
    }
  };

  // ---- Toggle a main category dropdown open/closed ----
  const handleMainCategoryClick = (category) => {
    const isCurrentlyOpen = openMainCatId === category.id;
    setOpenMainCatId(isCurrentlyOpen ? null : category.id);
    setSelectedItem(category.label || category.name);

    if (!isCurrentlyOpen) {
      fetchCollectionsForCat(category.id);
    }
  };

  // ---- Navigate to a collection page on click ----
  const handleCollectionClick = (collection, mainCatId) => {
    setOpenMainCatId(null);
    setIsOpen(false);
    navigate(`/collections/${collection.id}/${mainCatId}`);
  };

  const menuItems = [
    { href: "/", label: "Home" },
    { href: "App#services", label: "Services" },
    { href: "/gallery", label: "Gallery" },
    { href: "App#about", label: "About Us" },
    { href: "/contact", label: "Contact Us" },
    {
      label: "Partner with Us",
      href: "/partner",
    },
  ];

  if (["/measurement", "/measurementTabale"].includes(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* MAIN TOP HEADER */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-black/95 text-white shadow-lg backdrop-blur-md h-14 sm:h-16"
            : "bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm h-16 sm:h-20"
        }`}
      >
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-2">
          {/* Logo */}
          <div
            className="flex-shrink-0 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src={isScrolled ? goldlogo : logo}
              alt="NaturX Logo"
              className="h-8 sm:h-10 md:h-12 w-auto object-contain transition-all duration-300"
            />
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {menuItems.map((item) => (
              <div
                key={item.label}
                className="relative group py-2"
                onMouseEnter={() => {
                  if (hoverTimeout) clearTimeout(hoverTimeout);
                  setOpenSubMenu(item.label);
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => setOpenSubMenu(null), 300);
                  setHoverTimeout(timeout);
                }}
              >
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.subMenu) e.preventDefault();
                    else navigate(item.href);
                  }}
                  className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-[#d35400] flex items-center gap-1 whitespace-nowrap ${
                    selectedItem === item.label ? "text-[#d35400]" : ""
                  }`}
                >
                  {item.label}
                  {item.subMenu && <ChevronDown className="w-4 h-4" />}
                </a>

                {/* Dropdown Menu */}
                {item.subMenu && openSubMenu === item.label && (
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-60 sm:w-64 bg-white text-gray-800 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden transition-all duration-200 z-50">
                    {item.subMenu.map((subItem) => (
                      <button
                        key={subItem.label}
                        onClick={() => {
                          setSelectedItem(subItem.label);
                          setOpenSubMenu(null);
                          navigate(
                            `${subItem.href}/${subItem.id}/${subItem.label}`,
                          );
                        }}
                        className="w-full text-left px-4 py-3 text-xs font-medium hover:bg-gray-50 hover:text-[#d35400] border-b border-gray-100 last:border-none transition-colors"
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Action Items (Call, Cart, Mobile Toggle) */}
          <div className="flex items-center space-x-1.5 sm:space-x-3 md:space-x-4 flex-shrink-0">
            {data?.phone && (
              <a
                href={`tel:${data.phone}`}
                className={`hidden sm:flex items-center gap-1.5 sm:gap-2 text-xs font-bold px-2.5 sm:px-3 py-1.5 rounded-full border transition-all ${
                  isScrolled
                    ? "border-gray-700 text-white hover:bg-white hover:text-black"
                    : "border-gray-200 text-gray-700 hover:bg-gray-900 hover:text-white"
                }`}
              >
                <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="max-w-[90px] sm:max-w-[120px] truncate">
                  {data.phone}
                </span>
              </a>
            )}

            <button
              onClick={() => navigate("/cart")}
              aria-label="Cart"
              className={`relative p-1.5 sm:p-2 rounded-full transition-colors ${
                isScrolled ? "hover:bg-white/10" : "hover:bg-gray-100"
              }`}
            >
              <ShoppingBag className="h-5 w-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white">
                  {cartItemCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Menu"
              className={`lg:hidden p-1.5 sm:p-2 rounded-md transition-colors ${
                isScrolled ? "hover:bg-white/10" : "hover:bg-gray-100"
              }`}
            >
              {isOpen ? (
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* SECONDARY MAIN-CATEGORY ROW (Sits directly underneath Navbar) */}
      <div
        className={`fixed hidden lg:block left-0 right-0 z-40 transition-all duration-300 border-b ${
          isScrolled
            ? "bg-black/95 border-gray-800 text-white top-14 sm:top-16 shadow-lg backdrop-blur-md"
            : "bg-gray-50/95 border-gray-200/80 text-gray-800 top-16 sm:top-20 shadow-sm backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-2 grid grid-cols-5 gap-2.5 items-center">
          {mainCategories.map((category) => {
            const isOpenCat = openMainCatId === category.id;
            const rawTitle = category.label || category.name || "";
            const match = rawTitle.match(/^([^(]+)(?:\((.*)\))?$/);
            const mainTitle = match ? match[1].trim() : rawTitle;
            const subTitle = match && match[2] ? match[2].trim() : null;

            return (
              <button
                key={category.id}
                onClick={() => handleMainCategoryClick(category)}
                title={rawTitle}
                className={`w-full h-11 flex items-center justify-between px-3 py-1 rounded-xl text-left border transition-all duration-200 group relative overflow-hidden ${
                  isOpenCat
                    ? isScrolled
                      ? "border-[#d35400] bg-[#d35400]/25 text-[#d35400] ring-2 ring-[#d35400]/30 shadow-md font-bold"
                      : "border-[#d35400] bg-[#fff0e6] text-[#d35400] ring-2 ring-[#d35400]/25 shadow-md font-bold"
                    : isScrolled
                      ? "border-gray-800/80 bg-zinc-900/60 hover:border-[#d35400] hover:bg-zinc-800/80 text-gray-200"
                      : "border-gray-200 bg-white shadow-sm hover:border-[#d35400] hover:bg-[#fff7f2] text-gray-800"
                }`}
              >
                <div className="flex flex-col justify-center min-w-0 pr-1 leading-tight">
                  <span
                    className={`text-[11px] xl:text-[12px] font-bold truncate transition-colors ${
                      isOpenCat ? "text-[#d35400]" : "group-hover:text-[#d35400]"
                    }`}
                  >
                    {mainTitle}
                  </span>
                  {subTitle && (
                    <span className="text-[9px] xl:text-[10px] text-gray-400 truncate font-normal mt-0.5 opacity-90">
                      {subTitle}
                    </span>
                  )}
                </div>
                {isOpenCat ? (
                  <ChevronUp className="w-3.5 h-3.5 flex-shrink-0 text-[#d35400]" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:text-[#d35400] transition-all" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* MEGA DROPDOWN PANEL — rendered OUTSIDE the scrolling row so it can never
          be clipped. Positioned fixed, centered, below the category row. */}
      {openMainCatId !== null &&
        (() => {
          const activeCategory = mainCategories.find(
            (c) => c.id === openMainCatId,
          );
          const catState = collectionsByCat[openMainCatId];
          if (!activeCategory) return null;

          return (
            <>
              {/* click-away backdrop — all devices */}
              <div
                className="fixed inset-0 z-40 bg-black/30"
                onClick={() => setOpenMainCatId(null)}
              />

              <div
                className={`fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
                  isScrolled ? "top-[7rem] sm:top-32" : "top-36 sm:top-40"
                }`}
                style={{ width: "min(92vw, 720px)" }}
              >
                <div className="bg-white text-gray-900 rounded-2xl shadow-2xl ring-1 ring-black/5 p-3 sm:p-4 max-h-[70vh] overflow-y-auto">
                  {/* Header row with close button */}
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h2 className="text-sm font-bold text-gray-900">
                      {activeCategory.label || activeCategory.name}
                    </h2>
                    <button
                      onClick={() => setOpenMainCatId(null)}
                      aria-label="Close"
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {catState?.loading && (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Loading collections…
                    </div>
                  )}

                  {catState?.error && (
                    <div className="py-8 text-center text-sm text-red-500">
                      Couldn't load collections. Please try again.
                    </div>
                  )}

                  {!catState?.loading &&
                    !catState?.error &&
                    catState?.items?.length === 0 && (
                      <div className="py-8 text-center text-sm text-gray-500">
                        No collections found.
                      </div>
                    )}

                  {!catState?.loading && catState?.items?.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {catState.items.map((collection) => (
                        <button
                          key={collection.id}
                          onClick={() =>
                            handleCollectionClick(collection, openMainCatId)
                          }
                          className="text-left flex gap-3 items-center bg-white rounded-2xl sm:rounded-3xl p-2.5 sm:p-3 border border-gray-100
                                     hover:shadow-lg hover:border-[#d35400]/40
                                     active:scale-95 transition-all duration-300"
                        >
                          {/* IMAGE */}
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex-shrink-0 overflow-hidden bg-gray-100">
                            <img
                              src={collection.image}
                              alt={collection.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* TEXT */}
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 line-clamp-1">
                              {collection.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {collection.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}

      {/* MOBILE FULLSCREEN OVERLAY DRAWER */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />

      <div
        className={`fixed top-0 right-0 w-[85%] sm:w-4/5 max-w-sm h-full bg-white text-gray-800 z-50 shadow-2xl lg:hidden transition-transform duration-300 transform flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer close button */}
        <div className="flex items-center justify-end px-4 sm:px-6 pt-4 pb-1 flex-shrink-0">
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
            className="p-2 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
          <div className="flex flex-col space-y-3 sm:space-y-4">
            {menuItems.map((item) => (
              <div key={item.label} className="border-b border-gray-100 pb-2">
                <div
                  className="flex items-center justify-between font-bold text-gray-800 py-1 cursor-pointer"
                  onClick={() => {
                    if (item.subMenu) {
                      setOpenSubMenu(
                        openSubMenu === item.label ? null : item.label,
                      );
                    } else {
                      setIsOpen(false);
                      navigate(item.href);
                    }
                  }}
                >
                  <span className="text-sm sm:text-base">{item.label}</span>
                  {item.subMenu &&
                    (openSubMenu === item.label ? (
                      <ChevronUp className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ))}
                </div>

                {item.subMenu && openSubMenu === item.label && (
                  <div className="mt-2 pl-3 space-y-2 bg-gray-50 rounded-lg p-2 transition-all">
                    {item.subMenu.map((subItem) => (
                      <div
                        key={subItem.label}
                        onClick={() => {
                          setIsOpen(false);
                          navigate(
                            `${subItem.href}/${subItem.id}/${subItem.label}`,
                          );
                        }}
                        className="text-xs font-semibold text-gray-600 py-1.5 hover:text-[#d35400] cursor-pointer"
                      >
                        {subItem.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile main categories */}
          <div className="flex flex-col space-y-1 pt-2 border-t border-gray-100">
            <span className="text-[0.875rem] flex items-center justify-between font-bold text-gray-800 py-1 cursor-pointer">
              Collections
            </span>
            {mainCategories.map((category) => {
              const isOpenCat = openMainCatId === category.id;
              const rawTitle = category.label || category.name || "";
              const match = rawTitle.match(/^([^(]+)(?:\((.*)\))?$/);
              const mainTitle = match ? match[1].trim() : rawTitle;
              const subTitle = match && match[2] ? match[2].trim() : null;

              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setIsOpen(false);
                    handleMainCategoryClick(category);
                  }}
                  className="flex text-start justify-between items-center font-semibold text-gray-700 py-2.5 text-sm border-b border-gray-100 last:border-none hover:text-[#d35400] transition-colors"
                >
                  <div className="flex flex-col min-w-0 pr-2 leading-tight">
                    <span className="font-bold text-gray-800 text-xs sm:text-sm">{mainTitle}</span>
                    {subTitle && (
                      <span className="text-[10px] sm:text-xs text-gray-400 font-normal mt-0.5">{subTitle}</span>
                    )}
                  </div>
                  {isOpenCat ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0 text-[#d35400]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile Drawer Footer Contact */}
        {data?.phone && (
          <div className="bg-gray-50 p-3 sm:p-4 rounded-xl flex items-center justify-center gap-3 m-4 mt-0 flex-shrink-0">
            <Phone className="w-4 h-4 text-[#d35400] flex-shrink-0" />
            <a
              href={`tel:${data.phone}`}
              className="text-sm font-bold text-gray-800"
            >
              {data.phone}
            </a>
          </div>
        )}
      </div>

      {/* SPACER: Adjusts height based on device */}
      <div
        className={`transition-all duration-300 ${isScrolled ? "h-14 lg:h-28" : "h-16 lg:h-32"}`}
      ></div>

      {/* Global CSS Injecting for custom utilities */}
      <style jsx="true">{`
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};
